import { type Href, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import type { GeneratedGameTeam } from '@/components/Event/GeneratedGames/GeneratedGame.types';
import GeneratedSchedulePreview, {
  useGeneratedSchedulePreview,
} from '@/components/Event/GeneratedGames/GeneratedSchedulePreview';
import Box from '@/components/Unistyles/Box';
import WelcomeStepFrame from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import { WELCOME_COMPLETION_ROUTE } from '@/layouts/Welcome/navigation';
import generateGames from '@/utils/generateGames';

type WelcomeOrgSchedulePreviewProps = {
  completionRoute?: Href;
};

type WelcomeScheduleTeam = GeneratedGameTeam & {
  avatar_id: string | null;
  home_primary_color: string | null;
};

const GENERATED_SCHEDULE_FIELD_NAME = 'generatedScheduleGames';

function WelcomeScheduleRegenerateAction({
  teams,
}: {
  teams: WelcomeScheduleTeam[];
}) {
  const {
    actions: { replaceGames },
  } = useGeneratedSchedulePreview();
  const { control } = useFormContext<WelcomeFormValues>();
  const seasonDraftId = useWatch({ control, name: 'seasonDraftId' });
  const scheduleSlots = useWatch({ control, name: 'scheduleSlots' });
  const matchupCount = useWatch({ control, name: 'scheduleMatchupCount' });
  const repeatEveryWeeks = useWatch({
    control,
    name: 'scheduleRepeatEveryWeeks',
  });
  const filledSlots = scheduleSlots
    .filter((slot) => slot.datetime.trim().length > 0)
    .map((slot) => ({
      datetime: slot.datetime.trim(),
      location: slot.location.trim() || null,
    }));
  const canRegenerateSchedule =
    matchupCount > 0 &&
    repeatEveryWeeks > 0 &&
    Boolean(seasonDraftId) &&
    teams.length > 1 &&
    filledSlots.length > 0;

  return (
    <WelcomeSecondaryAction
      label="Regenerate"
      icon="refresh-outline"
      disabled={!canRegenerateSchedule}
      onPress={() => {
        if (!canRegenerateSchedule) {
          return;
        }

        const { games } = generateGames({
          parentId: seasonDraftId,
          teams,
          initialGames: filledSlots,
          numberOfMatchups: matchupCount,
          repeatEvery: { weeks: repeatEveryWeeks },
          byeWeeks: true,
        });

        replaceGames(games);
      }}
    />
  );
}

function WelcomeScheduleFooter({
  onPressContinue,
  teams,
}: {
  onPressContinue(): void;
  teams: WelcomeScheduleTeam[];
}) {
  return (
    <Box space="sm" style={styles.footerContent}>
      <GeneratedSchedulePreview.RosterTray />
      <WelcomeActions>
        <GeneratedSchedulePreview.EditAction />
        <WelcomeScheduleRegenerateAction teams={teams} />
        <GeneratedSchedulePreview.PrimaryAction
          label="Continue"
          onPress={onPressContinue}
        />
      </WelcomeActions>
    </Box>
  );
}

function WelcomeScheduleFrame({
  onPressContinue,
  teams,
}: {
  onPressContinue(): void;
  teams: WelcomeScheduleTeam[];
}) {
  const {
    state: { gameIds },
  } = useGeneratedSchedulePreview();

  return (
    <WelcomeStepFrame
      title="Review game dates"
      subtitle={`${gameIds.length} games will be created for your Season. You can change these at any time later.`}
      wide
      footer={
        <WelcomeScheduleFooter
          onPressContinue={onPressContinue}
          teams={teams}
        />
      }
    >
      <GeneratedSchedulePreview.Content
        datetimeTestIDPrefix="welcome-generated-game"
        invalidMessage="Use valid dates for every game before continuing."
      />
    </WelcomeStepFrame>
  );
}

export default function WelcomeOrgSchedulePreview({
  completionRoute = WELCOME_COMPLETION_ROUTE,
}: WelcomeOrgSchedulePreviewProps) {
  const { navigate } = useRouter();
  const { control } = useFormContext<WelcomeFormValues>();
  const teamRows = useWatch({ control, name: 'orgTeamRows' });
  const teams = teamRows
    .map((teamRow) => ({
      id: teamRow.id,
      title: teamRow.title.trim(),
      home_primary_color: teamRow.color ?? null,
      avatar_id: null,
    }))
    .filter((team) => team.title.length > 0);
  const onPressContinue = () => {
    navigate(completionRoute);
  };

  return (
    <GeneratedSchedulePreview.Provider
      fieldName={GENERATED_SCHEDULE_FIELD_NAME}
      teams={teams}
    >
      <WelcomeScheduleFrame onPressContinue={onPressContinue} teams={teams} />
    </GeneratedSchedulePreview.Provider>
  );
}

const styles = StyleSheet.create({
  footerContent: {
    width: '100%',
  },
});
